import { isKnown } from '../../store/assessmentFacts.js';
import { money } from './format.js';
import PortfolioAnalysisView from './PortfolioAnalysisView.jsx';
import { CashChart, TaxTriangle, SplitBar } from './pictures.jsx';

// The pictures as they were drawn before September 29, rebuilt from the
// figures saved with a drawing so older ink still lands on what it was about.
// Nothing here is shown in a meeting any more; it exists so a takeaway can
// print a legacy drawing over the page it was made on.

const BASIS =
  'Based on the information shown, not a complete financial plan. No taxes, inflation, investment returns or '
  + 'unrecorded withdrawals are modelled.';

function CashBoard({ facts, comparison }) {
  const spending = money(facts.spending);
  const income = money(facts.income);

  if (!comparison.ready) {
    return (
      <>
        <h2>First, bring the missing pieces into view.</h2>
        <div className="conv-empty">
          <div className="conv-eyebrow">A question worth clarifying</div>
          <h2>{!isKnown(facts.spending) ? 'What does a typical year of spending look like?' : 'What income should we include?'}</h2>
          <p>We can talk through what is recorded without filling in the blanks for you.</p>
          <div className="conv-coverage">
            <div><span>Annual spending</span><strong>{spending}</strong></div>
            <div><span>{facts.income.complete ? 'Annual income' : 'Known income so far'}</span><strong>{income}</strong></div>
          </div>
          <p className="conv-caption">No gap, surplus or funding conclusion is drawn from incomplete figures.</p>
        </div>
      </>
    );
  }
  return (
    <>
      <h2>What comes in, against what you spend.</h2>
      <div className="conv-board">
        <CashChart rows={facts.income.rows} income={comparison.income} spending={comparison.spending} period={facts.period} />
        <div className="conv-board-bottom">
          <div className="conv-board-figures">
            <div><span className="conv-quiet">Income</span><div className="conv-value">{income}</div></div>
            <div><span className="conv-quiet">Spending</span><div className="conv-value">{spending}</div></div>
            <div>
              <span className="conv-quiet">
                {comparison.direction === 'short' ? 'Spending above income'
                  : comparison.direction === 'above' ? 'Income above spending' : 'Income equals spending'}
              </span>
              <div className={`conv-value${comparison.direction === 'short' ? ' is-short' : ''}`}>
                {`${money(Math.abs(comparison.gap))} / year`}
              </div>
            </div>
          </div>
          <span className="conv-quiet">{facts.period}</span>
        </div>
      </div>
      <p className="conv-caption">{BASIS}</p>
    </>
  );
}

function LegacyReport({ facts, comparison, board, analysis }) {
  if (board === 'portfolio') {
    return (
      <PortfolioAnalysisView availability={analysis?.availability || { state: 'absent' }} exposure={analysis?.exposure}
        style={analysis?.style} context={analysis?.context} scope={analysis?.scope} simulated={analysis?.simulated} />
    );
  }
  if (board === 'tax') {
    const p = facts.pools;
    const v = f => (isKnown(f) ? f.value : null);
    const total = [p.taxDeferred, p.taxable, p.roth].filter(isKnown).reduce((s, f) => s + f.value, 0);
    return (
      <>
        <h2>Your assets by tax type</h2>
        <div className="conv-board">
          <TaxTriangle total={total} corners={[
            { key: 'taxDeferred', label: 'Tax-deferred', sub: 'Taxed as income when withdrawn', value: v(p.taxDeferred) },
            { key: 'taxable', label: 'Taxable', sub: 'Gains taxed as they are realised', value: v(p.taxable) },
            { key: 'roth', label: 'Roth', sub: 'Tax-free when the rules are met', value: v(p.roth) },
          ]} />
          <SplitBar parts={[
            { key: 'taxDeferred', label: 'Tax-deferred', value: v(p.taxDeferred), fill: '#015697' },
            { key: 'taxable', label: 'Taxable', value: v(p.taxable), fill: '#6fa3cf' },
            { key: 'roth', label: 'Roth', value: v(p.roth), fill: '#2f7d4f' },
          ]} />
        </div>
      </>
    );
  }
  return <CashBoard facts={facts} comparison={comparison} />;
}

/** The printed page for an older drawing: one picture, from the figures saved with it. */
export default function NoticedView({ facts, comparison, board, analysis }) {
  return (
    <>
      <div className="conv-eyebrow">What we noticed{facts.name ? ` · ${facts.name}` : ''}</div>
      <LegacyReport facts={facts} comparison={comparison} board={board} analysis={analysis} />
    </>
  );
}
